import { useCallback, useMemo } from "react";
import { useGetNationalityList, useGetResidenceStatusList, useGetLanguageTestList } from "@/api/services/global/master";
import { useEnum } from "@/api/services/global/enum";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { useTalentFilterOptions } from "@/api/services/main/talent";
import { truncateWithEllipsis } from "@/utils/string";
import { TOption } from "@/types/common";
import { CaseMatchStatusOptions } from "@/types/enum";
import { FILTER_LABEL_MAX_LENGTH } from "@/features/case/components/CaseEdit/case-edit-match-filter-shared";

/**
 * Hook to fetch all master data needed for case edit match filter dropdowns.
 * Returns both truncated options (for display in dropdowns) and raw options (for label resolution).
 */
export const useCaseEditMatchFilterMasterData = () => {
  const { nationalityOptions } = useGetNationalityList();
  const { residenceStatusOptions } = useGetResidenceStatusList();
  const { data: genderOptions } = useEnum("EGender");
  const { languageTestLevelOptions } = useGetLanguageTestList();
  const { userOptions: personInChargeOptions } = useGetUserListPersonInCharge();
  const { talentNumberOptions, fullNameOptions, nickNameOptions } = useTalentFilterOptions();

  // CaseMatchStatusOptions are static — convert numeric values to string for use with ComboboxMulti
  const statusOptions: TOption[] = useMemo(
    () => CaseMatchStatusOptions.map((opt) => ({ label: opt.label, value: String(opt.value) })),
    [],
  );

  const truncateOpts = useCallback(
    (options: TOption[], maxLength = FILTER_LABEL_MAX_LENGTH) => options.map((opt) => ({ ...opt, label: truncateWithEllipsis(opt.label, maxLength) })),
    [],
  );

  const truncatedOptions = useMemo(
    () => ({
      status: truncateOpts(statusOptions),
      talentNumber: truncateOpts(talentNumberOptions),
      fullName: truncateOpts(fullNameOptions),
      nickName: truncateOpts(nickNameOptions),
      nationality: truncateOpts(nationalityOptions),
      gender: truncateOpts(genderOptions ?? []),
      residenceStatus: truncateOpts(residenceStatusOptions ?? []),
      japaneseLevelType: truncateOpts(languageTestLevelOptions),
      personInCharge: truncateOpts(personInChargeOptions),
    }),
    [
      truncateOpts,
      statusOptions,
      talentNumberOptions,
      fullNameOptions,
      nickNameOptions,
      nationalityOptions,
      genderOptions,
      residenceStatusOptions,
      languageTestLevelOptions,
      personInChargeOptions,
    ],
  );

  const rawOptions = useMemo(
    () => ({
      status: statusOptions,
      nationality: nationalityOptions,
      gender: genderOptions ?? [],
      residenceStatus: residenceStatusOptions ?? [],
      japaneseLevelType: languageTestLevelOptions,
      personInCharge: personInChargeOptions,
    }),
    [statusOptions, nationalityOptions, genderOptions, residenceStatusOptions, languageTestLevelOptions, personInChargeOptions],
  );

  return { truncatedOptions, rawOptions };
};
